import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

// Pace of the typing, one letter at a time
const CHAR_MS = 38;

/**
 * Types a message out as the planner would: a short wait first, then one
 * letter at a time. Starts over on each mount. With reduced motion the
 * message is there at once.
 */
export const useTypedMessage = (message: string, waitMs: number) => {
  const reduceMotion = useReducedMotion();
  const [isWaiting, setIsWaiting] = useState(!reduceMotion);
  const [chars, setChars] = useState(reduceMotion ? message.length : 0);

  // Ends the wait
  useEffect(() => {
    if (!isWaiting) return;
    const timer = setTimeout(() => setIsWaiting(false), waitMs);
    return () => clearTimeout(timer);
  }, [isWaiting, waitMs]);

  // Adds one letter at a time until the message is complete
  useEffect(() => {
    if (isWaiting || chars >= message.length) return;
    const timer = setTimeout(() => setChars(chars + 1), CHAR_MS);
    return () => clearTimeout(timer);
  }, [isWaiting, chars, message.length]);

  return {
    isWaiting,
    typed: message.slice(0, chars),
    isComplete: !isWaiting && chars >= message.length,
  };
};
